<template>
  <div class="box">
    <h2 class="subtitle">
      <span class="icon"><i class="fa fa-user"></i></span>
      {{ $t("step 1 personal information") }}
    </h2>

    <div class="columns">
      <div class="column is-half">
        <div class="field">
          <label class="label">{{ $t("first names") }} *</label>
          <div class="control has-icons-left">
            <input
              class="input"
              :class="{ 'is-danger': errors.nombres }"
              type="text"
              :value="formData.nombres"
              @input="$emit('update:nombres', $event.target.value)"
              @blur="$emit('validate-field', 'nombres')"
              :placeholder="$t('enter your first names')"
              required
            />
            <span class="icon is-small is-left">
              <i class="fa fa-user"></i>
            </span>
          </div>
          <p v-if="errors.nombres" class="help is-danger">
            {{ errors.nombres }}
          </p>
        </div>
      </div>

      <div class="column is-half">
        <div class="field">
          <label class="label">{{ $t("last names") }} *</label>
          <div class="control has-icons-left">
            <input
              class="input"
              :class="{ 'is-danger': errors.apellidos }"
              type="text"
              :value="formData.apellidos"
              @input="$emit('update:apellidos', $event.target.value)"
              @blur="$emit('validate-field', 'apellidos')"
              :placeholder="$t('enter your last names')"
              required
            />
            <span class="icon is-small is-left">
              <i class="fa fa-user"></i>
            </span>
          </div>
          <p v-if="errors.apellidos" class="help is-danger">
            {{ errors.apellidos }}
          </p>
        </div>
      </div>
    </div>

    <div class="columns">
      <div class="column is-half">
        <div class="field">
          <label class="label">{{ $t("email") }} *</label>
          <div class="control has-icons-left">
            <input
              class="input"
              :class="{ 'is-danger': errors.email }"
              type="email"
              :value="formData.email"
              @input="$emit('update:email', $event.target.value)"
              @blur="$emit('validate-field', 'email')"
              :placeholder="$t('email placeholder')"
              required
            />
            <span class="icon is-small is-left">
              <i class="fa fa-envelope"></i>
            </span>
          </div>
          <p v-if="errors.email" class="help is-danger">
            {{ errors.email }}
          </p>
        </div>
      </div>

      <div class="column is-half">
        <div class="field">
          <label class="label">{{ $t("birth date") }} *</label>
          <div class="control has-icons-left">
            <input
              class="input"
              :class="{ 'is-danger': errors.fechaNacimiento }"
              type="date"
              :value="formData.fechaNacimiento"
              @input="$emit('update:fecha-nacimiento', $event.target.value)"
              @blur="$emit('validate-field', 'fechaNacimiento')"
              required
            />
            <span class="icon is-small is-left">
              <i class="fa fa-calendar"></i>
            </span>
          </div>
          <p v-if="errors.fechaNacimiento" class="help is-danger">
            {{ errors.fechaNacimiento }}
          </p>
        </div>
      </div>
    </div>

    <div class="columns">
      <div class="column is-half">
        <div class="field">
          <label class="label">{{ $t("phone number in usa") }} *</label>
          <div class="control has-icons-left">
            <input
              class="input"
              :class="{ 'is-danger': errors.telefonoUSA }"
              type="tel"
              :value="formData.telefonoUSA"
              @input="$emit('update:telefono-usa', $event.target.value)"
              @blur="$emit('validate-field', 'telefonoUSA')"
              placeholder="5551234567"
              required
            />
            <span class="icon is-small is-left">
              <i class="fa fa-phone"></i>
            </span>
          </div>
          <p v-if="errors.telefonoUSA" class="help is-danger">
            {{ errors.telefonoUSA }}
          </p>
        </div>
      </div>

      <div class="column is-half"></div>
    </div>

    <div class="columns">
      <div class="column is-one-third">
        <div class="field">
          <label class="label">{{ $t("zip code") }} *</label>
          <div
            class="control has-icons-left"
            :class="{ 'is-loading': isLoadingZip }"
          >
            <input
              class="input"
              :class="{ 'is-danger': errors.zipCode }"
              type="text"
              :value="formData.zipCode"
              @input="$emit('handle-zipcode-input', $event.target.value)"
              @blur="$emit('validate-field', 'zipCode')"
              placeholder="12345"
              maxlength="10"
              required
            />
            <span class="icon is-small is-left">
              <i class="fa fa-globe"></i>
            </span>
          </div>
          <p v-if="errors.zipCode" class="help is-danger">
            {{ errors.zipCode }}
          </p>
        </div>
      </div>

      <div class="column is-one-third">
        <div class="field">
          <label class="label">{{ $t("state") }}</label>
          <div class="control has-icons-left">
            <input
              class="input"
              type="text"
              :value="formData.estado"
              :placeholder="$t('will be completed automatically')"
              readonly
            />
            <span class="icon is-small is-left">
              <i class="fa fa-flag"></i>
            </span>
          </div>
        </div>
      </div>

      <div class="column is-one-third">
        <div class="field">
          <label class="label">{{ $t("city") }}</label>
          <div class="control has-icons-left">
            <input
              class="input"
              type="text"
              :value="formData.ciudad"
              :placeholder="$t('will be completed automatically')"
              readonly
            />
            <span class="icon is-small is-left">
              <i class="fa fa-map-marker"></i>
            </span>
          </div>
        </div>
      </div>
    </div>

    <div class="columns">
      <div class="column is-half">
        <div class="field">
          <label class="label">{{ $t("monthly income") }} *</label>
          <div class="control has-icons-left">
            <input
              class="input"
              :class="{ 'is-danger': errors.ingresosMensuales }"
              type="number"
              min="0"
              step="0.01"
              :value="formData.ingresosMensuales"
              @input="
                $emit(
                  'update:ingresos-mensuales',
                  parseFloat($event.target.value) || 0
                )
              "
              @blur="$emit('validate-field', 'ingresosMensuales')"
              placeholder="1000"
              required
            />
            <span class="icon is-small is-left">
              <i class="fa fa-usd"></i>
            </span>
          </div>
          <p v-if="errors.ingresosMensuales" class="help is-danger">
            {{ errors.ingresosMensuales }}
          </p>
        </div>
      </div>

      <div class="column is-half">
        <div class="field">
          <label class="label">{{ $t("requested loan amount") }} *</label>
          <div class="control has-icons-left">
            <input
              class="input"
              :class="{ 'is-danger': errors.montoCredito }"
              type="number"
              min="0"
              step="0.01"
              :value="formData.montoCredito"
              @input="
                $emit(
                  'update:monto-credito',
                  parseFloat($event.target.value) || 0
                )
              "
              @blur="$emit('validate-field', 'montoCredito')"
              placeholder="5000"
              required
            />
            <span class="icon is-small is-left">
              <i class="fa fa-usd"></i>
            </span>
          </div>
          <p v-if="errors.montoCredito" class="help is-danger">
            {{ errors.montoCredito }}
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "PersonalInformationStep",
  props: {
    formData: {
      type: Object,
      required: true,
    },
    errors: {
      type: Object,
      required: true,
    },
    isLoadingZip: {
      type: Boolean,
      default: false,
    },
  },
  emits: [
    "update:nombres",
    "update:apellidos",
    "update:email",
    "update:fecha-nacimiento",
    "update:telefono-usa",
    "validate-field",
    "handle-zipcode-input",
    "update:ingresos-mensuales",
    "update:monto-credito",
  ],
};
</script>
